/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid ui/toolbar.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

import type { CsvDownloadOptions, CsvTable } from '../util/csv'
import type { DataGridLabels, DataGridOptions } from '../types'

const CLASS_NAME_TOOLBAR = 'data-grid-toolbar'
const CLASS_NAME_ACTIONS = 'data-grid-toolbar-actions'
const CLASS_NAME_SEARCH = 'data-grid-toolbar-search'

export interface GridToolbarContext {
  labels: DataGridLabels
  columns: boolean
  export: boolean
  // Undo/redo of edit commits - both buttons start disabled, the grid
  // toggles them as the history stacks fill.
  history: boolean
  print: boolean
  search: boolean
  icons: {
    columns: string
    export: string
    print: string
    redo: string
    undo: string
  }
}

export interface GridToolbar {
  element: HTMLDivElement
}

export type DataGridExporter = (table: CsvTable, options: DataGridExportOptions) => void

export interface DataGridExportOptions extends CsvDownloadOptions {
  // Sheet name for spreadsheet writers; the CSV writer ignores it.
  sheetName?: string
  // The writer the toolbar button calls, defaulting to the built-in CSV one.
  // Pass `downloadXlsx` from `@coreui/data-grid/xlsx` for Excel. It is
  // INJECTED, never imported by the grid: a writer only reaches the app
  // bundle when the app asks for it.
  exporter?: DataGridExporter
}

export interface ResolvedToolbar {
  columns: boolean
  export: boolean
  exportOptions: DataGridExportOptions
  history: boolean
  print: boolean
  search: boolean
}

// Normalizes `toolbar` + `globalFilter` into the individual actions. `toolbar:
// true` enables each action whose underlying feature is on; a granular object
// picks individually. `globalFilter: true` stays shorthand for the search input.
export const resolveToolbar = (options: DataGridOptions): ResolvedToolbar => {
  const { toolbar } = options
  const columnVisibility = Boolean(options.columnVisibility)

  if (toolbar === true) {
    return {
      columns: columnVisibility,
      export: true,
      exportOptions: {},
      history: options.history,
      print: true,
      search: true
    }
  }

  if (typeof toolbar === 'object') {
    const exportOption = toolbar.export ?? false
    return {
      columns: (toolbar.columns ?? false) && columnVisibility,
      export: Boolean(exportOption),
      exportOptions: typeof exportOption === 'object' ? exportOption : {},
      history: (toolbar.history ?? false) && options.history,
      print: toolbar.print ?? false,
      search: (toolbar.search ?? false) || Boolean(options.globalFilter)
    }
  }

  return {
    columns: false,
    export: false,
    exportOptions: {},
    history: false,
    print: false,
    search: Boolean(options.globalFilter)
  }
}

export const isGlobalFilterEnabled = (options: DataGridOptions): boolean =>
  resolveToolbar(options).search

const createActionButton = (icon: string, label: string, dataAttribute: string): HTMLButtonElement => {
  const button = document.createElement('button')
  button.type = 'button'
  button.classList.add('btn', 'btn-ghost', 'btn-sm')
  button.innerHTML = icon
  button.setAttribute(dataAttribute, '')
  button.setAttribute('data-coreui-tooltip', label)
  const text = document.createElement('span')
  text.classList.add('visually-hidden')
  text.textContent = label
  button.append(text)
  return button
}

export const createToolbar = (context: GridToolbarContext): GridToolbar => {
  const element = document.createElement('div')
  element.classList.add(CLASS_NAME_TOOLBAR)

  if (context.columns || context.export || context.print || context.history) {
    const actions = document.createElement('div')
    actions.classList.add(CLASS_NAME_ACTIONS)

    if (context.columns) {
      actions.append(
        createActionButton(context.icons.columns, context.labels.toolbarColumns, 'data-coreui-toolbar-columns')
      )
    }

    if (context.export) {
      actions.append(
        createActionButton(context.icons.export, context.labels.toolbarExport, 'data-coreui-toolbar-export')
      )
    }

    if (context.print) {
      actions.append(
        createActionButton(context.icons.print, context.labels.toolbarPrint, 'data-coreui-toolbar-print')
      )
    }

    if (context.history) {
      const undoButton = createActionButton(context.icons.undo, context.labels.toolbarUndo, 'data-coreui-toolbar-undo')
      const redoButton = createActionButton(context.icons.redo, context.labels.toolbarRedo, 'data-coreui-toolbar-redo')
      undoButton.disabled = true
      redoButton.disabled = true
      actions.append(undoButton, redoButton)
    }

    element.append(actions)
  }

  if (context.search) {
    const search = document.createElement('div')
    search.classList.add(CLASS_NAME_SEARCH)

    const input = document.createElement('input')
    input.type = 'search'
    input.classList.add('form-control', 'form-control-sm')
    input.placeholder = context.labels.globalFilterPlaceholder
    input.setAttribute('aria-label', context.labels.globalFilterLabel)
    input.dataset.coreuiGlobalFilter = ''

    search.append(input)
    element.append(search)
  }

  return { element }
}
